import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import OhsInjuryMgmtRecord, {
  OhsInjuryMgmtTemplateListPayload,
  OhsInjuryMgmtTemplateRecord,
} from 'injury-mgmt/models/OhsInjuryMgmtRecord';
import { RootState } from 'app/store';
import { ApiResListsType, OhsApiRequestName } from 'global-services/api/OhsApiModels';
import handleRegisterAsyncData, {
  handleRegisterCountAsyncData,
} from 'global-components/register/OhsModuleRegisterUtils';
import { ModuleType, TierType } from 'global-services/constants/OhsObject';
import { OhsUser } from 'user/OhsUser';
import OhsUserLocalServices from 'user/OhsUserLocalServices';
import { listActiveTaskFor } from 'task/OhsTaskServices';

import {
  getInjuryMgmtTemplateAllocatedList,
  fetchInjuryMgmtTemplatesByIds,
  getInjuryMgmtRecordList,
  getInjuryMgmtTemplateList,
} from './OhsInjuryMgmtServices';
import {
  getFolderAllocationListByIds,
  getInjuryMgmtFolderLists,
  searchInjuryMgmtFolderLists,
} from './folder/services/OhsInjuryMgmtFolderServices';
import OhsInjuryMgmtFilterModels from '../dashboard/topFilter/OhsInjuryMgmtFilterModels';
import { parseListAllocations } from './OhsInjuryMgmtUtils';

// State interface
interface InjuryMgmtState {
  isLoading: boolean;
  injuryMgmt: ApiResListsType<OhsInjuryMgmtRecord[]> | null;
  currentPage: number;
}

const injuryRecordListRPC = async (
  payload: OhsInjuryMgmtTemplateListPayload,
  currentUser: OhsUser
): Promise<ApiResListsType<OhsInjuryMgmtRecord[]> | null> => {
  const userTier = currentUser?.tierNum ?? 0;
  if (
    userTier &&
    (userTier === 3 || userTier === 4) &&
    currentUser?.configs?.injurymgmt?.PERMISSIONS?.view_submaster_record
  ) {
    const response = await getInjuryMgmtRecordList(payload);
    if (response && response.items.length > 0) {
      const idList = response.items.map((item: any) => item._id);
      const resTaskList: { _id: string; task: { _id: string } }[] = await listActiveTaskFor(idList);
      const taskIDHolder: any = {};
      resTaskList?.forEach((task) => {
        taskIDHolder[task._id] = { taskActive: true, taskId: task?.task?._id };
      });
      response.items = response.items.map((item: any) => {
        const taskInfo = taskIDHolder[item._id];
        return {
          ...item,
          taskActive: taskInfo?.taskActive || false,
          taskId: taskInfo?.taskId || '',
        };
      });
    }
    return response;
  }
  if (
    userTier &&
    userTier === 5 &&
    currentUser?.configs?.injurymgmt?.PERMISSIONS?.view_submaster_record
  ) {
    const response = await getInjuryMgmtRecordList(payload);
    return response;
  }
  return null;
};

export const getInjuryMgmtTemplateWithAllocations = (
  payload: any
): Promise<ApiResListsType<any[]> | null> => {
  const getInjuryMgmtTemplateListsReq = async (): Promise<ApiResListsType<any[]> | null> => {
    const injuryMgmtTemplateLists = await getInjuryMgmtTemplateList(payload);

    if (injuryMgmtTemplateLists && injuryMgmtTemplateLists.items.length === 0) {
      return Promise.resolve(injuryMgmtTemplateLists);
    }

    const templateIds: string[] = [];
    injuryMgmtTemplateLists?.items.forEach((item) => {
      if (item._id && item.tier.type === TierType.T3) {
        templateIds.push(item._id);
      }
    });

    if (templateIds.length === 0) {
      return Promise.resolve(injuryMgmtTemplateLists);
    }

    const allocatedTemplates = await getInjuryMgmtTemplateAllocatedList(templateIds);

    const templateListWithAllocations = parseListAllocations(
      injuryMgmtTemplateLists,
      allocatedTemplates
    ) as ApiResListsType<OhsInjuryMgmtTemplateRecord[]> | null;

    return Promise.resolve(templateListWithAllocations);
  };

  return getInjuryMgmtTemplateListsReq();
};

const injuryTemplateListRPC = async (
  payload: OhsInjuryMgmtTemplateListPayload,
  currentUser: OhsUser
): Promise<ApiResListsType<OhsInjuryMgmtRecord[]> | null> => {
  const userTier = currentUser?.tierNum ?? 0;
  if (
    userTier &&
    (userTier === 3 || userTier === 4) &&
    currentUser?.configs?.injurymgmt?.PERMISSIONS?.view_template
  ) {
    const templateListPayload = {
      ...payload,
      filter: {
        ...payload.filter,
        viewPreset: userTier === 3 ? 'view_1' : 'view_3',
      },
    };
    const response = await getInjuryMgmtTemplateWithAllocations(templateListPayload);
    return response;
  }
  return null;
};

const initialState: InjuryMgmtState = {
  isLoading: false,
  injuryMgmt: null,
  currentPage: 1,
};

// Helper to extract filters
const injuryMgmtFilterOptions = (
  state: RootState,
  filterKey: keyof OhsInjuryMgmtFilterModels
): any => {
  const { globalfilter } = state;
  const filter = globalfilter.filterInfo;
  if (filterKey === 'template') {
    return {
      archived: filter.injuryMgmtModule[filterKey].archived,
      workplaces: filter.workplaces,
      categories: filter.injuryMgmtModule[filterKey].categories,
      subcategories: filter.injuryMgmtModule[filterKey].subcategories,
      templateTypes: filter.injuryMgmtModule[filterKey].templateTypes,
      allocatedToWorkplace: filter.injuryMgmtModule[filterKey].allocatedToWorkplace,
      notAllocated: filter.injuryMgmtModule[filterKey].notAllocated,
      workplaceOwned: filter.injuryMgmtModule[filterKey].workplaceOwned,
    };
  }
  return {
    archived: filter.injuryMgmtModule[filterKey].archived,
    workplaces: filter.workplaces,
  };
};

// Thunk: Fetch list
export const fetchInjuryMgmtListAsync = createAsyncThunk<
  ApiResListsType<OhsInjuryMgmtRecord[]> | null,
  {
    hasSearch?: boolean;
    list?: any;
    count?: any;
    filterKey: keyof OhsInjuryMgmtFilterModels;
    folderId?: string;
  },
  { state: RootState }
>('injuryMgmt/fetchList', async ({ hasSearch, list, filterKey, folderId }, thunkAPI) => {
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;
  const state = thunkAPI.getState();
  const filter = state.globalfilter.filterInfo;

  const payload: OhsInjuryMgmtTemplateListPayload = {
    forType: ModuleType.Injury,
    filter: {
      ...injuryMgmtFilterOptions(state, filterKey),
      page: state.injuryMgmt.currentPage > 10 ? 0 : state.injuryMgmt.currentPage,
      sort: { ...JSON.parse(String(filter.injuryMgmtModule?.[filterKey]?.sort)) },
      viewPreset: user.tierNum === 3 ? 'view_1' : 'view_3',
      count: false,
    },
  };

  if (folderId) {
    payload._id = folderId;
  }

  try {
    if (filterKey === 'folder') {
      if (hasSearch) {
        const response = await searchInjuryMgmtFolderLists(list);
        return response;
      }
      list.viewPreset = user.tierNum === 3 ? 'view_1' : 'view_2';
      const response = await getInjuryMgmtFolderLists(list);
      // Map and collect getFolderAllocationList objects
      if (response && user?.tier?.type === TierType.T3) {
        let allocationList: any[] = [];
        try {
          const result = await getFolderAllocationListByIds(
            response?.items.map((item) => item._id as string)
          );
          if (Array.isArray(result)) {
            allocationList = result;
          }
        } catch (error) {
          // If the allocation list request fails, leave allocationList as an empty array.
        }

        if (Array.isArray(allocationList) && allocationList.length > 0) {
          response.items.forEach((item: OhsInjuryMgmtRecord) => {
            item.subRows = allocationList.filter(
              (allocItem: any) => allocItem?.allocationOf?._id === item._id
            );
          });
        }
      }
      return response;
    }

    if (filterKey === 'record') {
      const recordPayload: OhsInjuryMgmtTemplateListPayload = {
        ...payload,
        filter: {
          ...payload.filter,
          viewPreset: folderId ? 'view_1' : 'view_2',
        },
      };
      const response = await injuryRecordListRPC(recordPayload, user);
      const templateIds = new Set<string>();
      const folderIds = new Set<string>();
      response?.items?.forEach((item) => {
        if (item.recordTemplate?.type === ModuleType.InjuryTemplate) {
          templateIds.add(item.recordTemplate._id);
        }
        if (item.owner) folderIds.add(item.owner._id);
      });
      let folderRes: any[] = [];
      let templateRes: any[] = [];
      if (templateIds.size > 0) {
        try {
          templateRes = await fetchInjuryMgmtTemplatesByIds(
            Array.from(templateIds),
            OhsApiRequestName.TemplateFetch
          );
        } catch (error) {
          console.error('Failed to fetch template details:', error);
        }
      }
      if (folderIds.size > 0 && !folderId) {
        try {
          folderRes = await fetchInjuryMgmtTemplatesByIds(
            Array.from(folderIds),
            OhsApiRequestName.MasterFetch
          );
        } catch (error) {
          console.error('Failed to fetch folder details:', error);
        }
      }
      response?.items?.forEach((item) => {
        const templateMatch = templateRes.find((t) => t._id === item.recordTemplate?._id);
        if (templateMatch && item.recordTemplate) {
          item.recordTemplate.title = templateMatch.title;
        }
        const folderMatch = folderRes.find((f) => f._id === item.owner?._id);
        if (folderMatch && item.owner) {
          item.owner.title = folderMatch.title;
          item.folderStatus = folderMatch.status;
        }
      });
      return response;
    }

    const response = await injuryTemplateListRPC(payload, user);
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response?.data);
  }
});

// Thunk: Fetch count
export const fetchInjuryMgmtCountAsync = createAsyncThunk<
  ApiResListsType<OhsInjuryMgmtRecord[]> | null,
  {
    hasSearch?: boolean;
    list?: any;
    count?: any;
    filterKey: keyof OhsInjuryMgmtFilterModels;
    folderId?: string;
  },
  { state: RootState }
>('injuryMgmt/fetchCount', async ({ hasSearch, count, filterKey, folderId }, thunkAPI) => {
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;

  const state = thunkAPI.getState();
  const filter = state.globalfilter.filterInfo;

  const payload: OhsInjuryMgmtTemplateListPayload = {
    forType: ModuleType.Injury,
    filter: {
      ...injuryMgmtFilterOptions(state, filterKey),
      page: state.injuryMgmt.currentPage > 10 ? 0 : state.injuryMgmt.currentPage,
      sort: { ...JSON.parse(String(filter.injuryMgmtModule?.[filterKey]?.sort)) },
      viewPreset: user.tierNum === 3 ? 'view_1' : 'view_3',
      count: true,
    },
  };

  if (folderId) {
    payload._id = folderId;
  }

  try {
    if (filterKey === 'folder') {
      if (hasSearch) {
        const response = await searchInjuryMgmtFolderLists(count);
        return response;
      }
      count.viewPreset = user.tierNum === 3 ? 'view_1' : 'view_2';
      const response = await getInjuryMgmtFolderLists(count);
      return response;
    }

    if (filterKey === 'record') {
      const recordPayload: OhsInjuryMgmtTemplateListPayload = {
        ...payload,
        filter: {
          ...payload.filter,
          viewPreset: folderId ? 'view_1' : 'view_2',
        },
      };
      const response = await getInjuryMgmtRecordList(recordPayload);
      return response;
    }

    const response = await getInjuryMgmtTemplateList(payload);
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response?.data);
  }
});

// Slice
export const injuryMgmtSlice = createSlice({
  name: 'injuryMgmt',
  initialState,
  reducers: {
    setInjuryMgmtCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInjuryMgmtListAsync.pending, (state) => {
        state.isLoading = true;
        state.injuryMgmt = {
          items: [],
          pagination: state.injuryMgmt?.pagination ?? {
            page: 0,
            totalPages: 0,
            next: '',
          },
        };
      })
      .addCase(fetchInjuryMgmtListAsync.fulfilled, (state, action) => {
        if (action.payload) {
          const list = handleRegisterAsyncData(action.payload, 'injuryMgmtTotalPages');
          state.injuryMgmt = list;
          state.currentPage = list.pagination.page ?? 1;
          state.isLoading = false;
        }
      })
      .addCase(fetchInjuryMgmtListAsync.rejected, (state) => {
        state.injuryMgmt = null;
        state.isLoading = false;
      })
      .addCase(fetchInjuryMgmtCountAsync.fulfilled, (state, action) => {
        if (action.payload) {
          const listWithCount = handleRegisterCountAsyncData(
            action.payload,
            state.injuryMgmt,
            'injuryMgmtTotalPages'
          );
          state.injuryMgmt = listWithCount;
        }
      });
  },
});

// Selectors
const injuryMgmtState = (state: RootState) => state.injuryMgmt;

export const getOhsInjuryMgmtModuleState = createSelector(
  [injuryMgmtState],
  (injuryMgmt) => injuryMgmt
);

export const { setInjuryMgmtCurrentPage } = injuryMgmtSlice.actions;
export const injuryMgmtReducer = injuryMgmtSlice.reducer;
